import { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';

import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { InsertionOrder, LineItem } from 'sonata-static-storage/lib/entities';
import type { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

const MAX_DISTRIBUTION = 100;

export type PreviewPosition = { lat: number; lng: number };

const DEFAULT_PREVIEW_POSITION: PreviewPosition = { lat: 0, lng: 0 };

type GridColumn = {
  area: string;
  size: string;
  secondRowArea?: string;
};

export type AdCreativesVisibleColumns = {
  adPrice: boolean;
  adTypeScreens: boolean;
  behavior: boolean;
  distribution: boolean;
  preview: boolean;
  qr: boolean;
};

export type AdCreativesGridLayout = {
  columns: string;
  headerAreas: string;
  rowAreas: string;
  visible: AdCreativesVisibleColumns;
};

export const getAdCreativesGridLayout = ({
  entityType,
  family,
  showAdLink,
  showAdPrice,
  showAdPriceColumn,
  showDistribution,
  showDistributionColumn
}: {
  entityType: Omit<MultiFlightEntityType, 'campaignPlan'>;
  family: CampaignFamilyEnum;
  showAdLink: boolean;
  showAdPrice: boolean;
  showAdPriceColumn: boolean;
  showDistribution: boolean;
  showDistributionColumn: boolean;
}): AdCreativesGridLayout => {
  const isDooh = family === 'dooh';
  const visible: AdCreativesVisibleColumns = {
    adPrice: showAdPrice && showAdPriceColumn,
    adTypeScreens: isDooh,
    behavior: !isDooh,
    distribution: entityType === 'lineItem' && showDistribution && showDistributionColumn,
    preview: !isDooh && showAdLink,
    qr: isDooh
  };

  const columns: GridColumn[] = [
    { area: 'title', secondRowArea: 'subtitle', size: 'minmax(120px, 3fr)' },
    { area: 'duration', size: '72px' },
    ...(visible.behavior ? [{ area: 'behavior', size: '120px' }] : []),
    ...(visible.qr ? [{ area: 'qr', size: '88px' }] : []),
    ...(visible.adTypeScreens ? [{ area: 'adTypeScreens', size: 'minmax(80px, 2fr)' }] : []),
    { area: 'group', size: 'minmax(120px, 2fr)' },
    ...(visible.distribution ? [{ area: 'distribution', size: '272px' }] : []),
    ...(visible.adPrice ? [{ area: 'adPrice', size: '80px' }] : []),
    { area: 'adStatus', size: '160px' },
    ...(visible.preview ? [{ area: 'preview', secondRowArea: '.', size: '24px' }] : []),
    { area: 'switch', secondRowArea: '.', size: '36px' },
    { area: 'actions', size: '24px' }
  ];

  const firstRow = columns.map(({ area }) => area).join(' ');
  const secondRow = columns.map(({ area, secondRowArea }) => secondRowArea ?? area).join(' ');

  return {
    columns: columns.map(({ size }) => size).join(' '),
    headerAreas: `"${firstRow}"`,
    rowAreas: `"${firstRow}" "${secondRow}"`,
    visible
  };
};

export const buildPreviewUrl = (
  ad: AdInterface<string>,
  entity: InsertionOrder | LineItem,
  entityType: Omit<MultiFlightEntityType, 'campaignPlan'>,
  { lat, lng }: PreviewPosition = DEFAULT_PREVIEW_POSITION
): string => {
  const parentParam = entityType === 'insertionOrder' ? `insertionOrderId=${entity._id}` : `campaignId=${entity._id}`;

  return `${taptap.config.landingsUrl}/display/preview/${ad._id}?${parentParam}&lat=${lat}&lng=${lng}&noCache=true&preview=true`;
};

const getAdsTotalDistribution = (
  ads: AdInterface<string>[],
  updatedAd?: {
    _id: string;
    distribution: number;
  }
): number =>
  ads.reduce((acc, ad) => {
    if (ad.deleted || !ad.visible) {
      return acc;
    }
    const currentDistribution = ad._id === updatedAd?._id ? updatedAd?.distribution : ad.distribution;
    return acc + (currentDistribution || 0);
  }, 0);

export const equalizeAdsDistribution = (
  currentAds: AdInterface<string>[],
  updatedAd?: {
    _id: string;
    distribution: number;
  }
): AdInterface<string>[] => {
  const activeAds = currentAds.filter(ad => !ad.deleted && ad.visible);

  if (activeAds.length === 0) {
    return currentAds;
  }

  if (!updatedAd) {
    const equalDistribution = Math.floor(MAX_DISTRIBUTION / activeAds.length);
    let remainder = MAX_DISTRIBUTION % activeAds.length;
    return currentAds.map(ad => {
      if (!ad.deleted && ad.visible) {
        let distribution = equalDistribution;
        if (remainder > 0) {
          distribution += 1;
          remainder--;
        }
        return { ...ad, distribution };
      }
      return ad;
    });
  }

  const totalDistribution = getAdsTotalDistribution(activeAds, updatedAd);
  const difference = MAX_DISTRIBUTION - totalDistribution;
  const adsToAdjust = activeAds.filter(ad => ad._id !== updatedAd?._id);

  if (adsToAdjust.length === 0) {
    return currentAds.map(ad => (ad._id === updatedAd?._id ? { ...ad, distribution: updatedAd?.distribution } : ad));
  }

  const changePerAd = Math.floor(difference / adsToAdjust.length);
  let remainder = difference % adsToAdjust.length;

  const newAds = currentAds.map(ad => {
    if (ad._id === updatedAd?._id) {
      return { ...ad, distribution: updatedAd?.distribution };
    }

    const isActiveAndAdjustable = !ad.deleted && ad.visible && ad._id !== updatedAd?._id;

    if (isActiveAndAdjustable) {
      let newDistribution = ad.distribution + changePerAd;

      if (remainder > 0) {
        newDistribution += 1;
        remainder--;
      }

      newDistribution = Math.max(0, newDistribution);

      return { ...ad, distribution: newDistribution };
    }

    return ad;
  });

  return newAds;
};
